/** Pre-bootstrap handoff: a valid native capability is required before replacing any tenant browser session. */
export class PurlivoSupportSession {
    static async enter(capability: string, tenantId: number, api: string): Promise<any> {
        history.replaceState(null, '', location.pathname);
        if (location.origin !== 'https://app-dev.purlivo.com' || !Number.isSafeInteger(tenantId) || tenantId < 1 ||
            !/^[0-9a-f-]{36}$/i.test(capability) || !navigator.locks) throw new Error('Purlivo handoff unavailable');
        const previousToken = abp.auth.getToken();
        const previousTenant = abp.multiTenancy.getTenantIdCookie();
        // The capability was removed before any API or dynamically loaded resource can receive it as a referrer.
        return navigator.locks.request('Purlivo.tenant.AccountSwitch', { mode: 'exclusive', ifAvailable: true }, async lock => {
            if (!lock || previousToken !== abp.auth.getToken()) throw new Error('Purlivo context changed');
            const result = await this.request(api + '/api/TokenAuth/ImpersonatedAuthenticate?impersonationToken=' + encodeURIComponent(capability), tenantId);
            try {
                if (previousToken !== abp.auth.getToken()) throw new Error('Purlivo context changed');
                if (previousToken) await this.logout(api, previousTenant, previousToken);
                if (previousToken !== abp.auth.getToken()) throw new Error('Purlivo context changed');
                abp.auth.clearRefreshToken();
                abp.utils.deleteCookie('abp_tenancy_name');
                abp.multiTenancy.setTenantIdCookie(tenantId);
                abp.auth.setToken(result.accessToken);
                return result;
            } catch (error) {
                // A minted delegate is not left usable when replacement of the previous context fails.
                await this.logout(api, tenantId, result.accessToken).catch(() => undefined);
                throw error;
            }
        });
    }

    private static async logout(api: string, tenantId: number | null, token: string): Promise<void> {
        await this.request(api + '/api/TokenAuth/LogOut', tenantId, token);
    }

    private static async request(url: string, tenantId: number | null, token?: string): Promise<any> {
        const headers: Record<string, string> = { 'Content-Type': 'application/json' };
        if (tenantId != null) headers['Abp-TenantId'] = String(tenantId);
        if (token) headers['Authorization'] = 'Bearer ' + token;
        const response = await fetch(url, { method: token ? 'GET' : 'POST', headers,
            credentials: 'omit', referrerPolicy: 'no-referrer', signal: AbortSignal.timeout(30000) });
        if (token && response.status === 401) return;
        if (!response.ok) throw new Error('Purlivo native authentication failed');
        const body = await response.json();
        if (body.success === false || (!token && !body.result?.accessToken)) throw new Error('Purlivo native authentication failed');
        return body.result;
    }
}
